import { Card, Flex, Typography } from 'antd';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import BaseEmpty from 'components/atoms/oneplatform/base-empty';

import ChartCanvas from './chart-canvas';
import ChartLegend from './chart-legend';
import ChartSkeleton from './chart-skeleton';
import styles from './styles.module.scss';
import { collectValidValues, hasChartData, sanitizeSeries } from './utils';

import type { JSX } from 'react';
import type { ITrendingChartProps } from './types';

const { Text } = Typography;

const TrendingChart = memo(
  ({
    title,
    categories = [],
    series = [],
    maxValue,
    minValue,
    height = 220,
    className,
    legendFormatter,
    emptyMessage = 'No trend data available',
    isLoading,
    isFetching,
    axisTitle = 'Number of readings',
  }: ITrendingChartProps) => {
    const sanitizedSeries = useMemo(
      () => sanitizeSeries(series ?? []),
      [series],
    );

    const validValues = useMemo(
      () => collectValidValues(sanitizedSeries),
      [sanitizedSeries],
    );

    const computedMax = useMemo(() => {
      if (typeof maxValue === 'number') return maxValue;
      return validValues.length > 0 ? Math.max(...validValues) : 1;
    }, [maxValue, validValues]);

    const computedMin = useMemo(() => {
      if (typeof minValue === 'number') return minValue;
      return validValues.length > 0 ? Math.min(...validValues, 0) : 0;
    }, [minValue, validValues]);

    const hasData = hasChartData(sanitizedSeries);
    const showSkeleton = Boolean(isLoading || isFetching);
    let content: JSX.Element;

    if (showSkeleton) {
      content = <ChartSkeleton />;
    } else if (hasData) {
      content = (
        <div className={styles['chart-wrapper']}>
          <ChartCanvas
            categories={categories}
            series={sanitizedSeries}
            maxValue={computedMax}
            minValue={computedMin}
            height={height}
            ariaLabel={title}
          />

          <ChartLegend
            series={sanitizedSeries}
            legendFormatter={legendFormatter}
          />
        </div>
      );
    } else {
      content = (
        <div className={styles['empty-state']}>
          <BaseEmpty description={emptyMessage} />
        </div>
      );
    }

    return (
      <Card
        className={classNames(styles['trending-card'], className)}
        variant="borderless"
        role="region"
        aria-label={title}
      >
        <Flex align="center" justify="space-between" gap={1}>
          {title && <Text className={styles['card-title']}>{title}</Text>}
          {axisTitle && (
            <Text className={styles['card-axis-title']}>{axisTitle}</Text>
          )}
        </Flex>
        {content}
      </Card>
    );
  },
);

TrendingChart.displayName = 'TrendingChart';

export default TrendingChart;
